import { useState, useEffect } from "react";
import {
  BarChart3,
  Plus,
  Trash2,
  Edit,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Users,
  RefreshCw,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  Award,
  ExternalLink
} from "lucide-react";
import { API, authHeaders } from "./utils";

export const HomepagePollTab = () => {
  const [polls, setPolls] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    options: ["", ""],
    expires_at: "",
    sponsor_id: "",
    sponsor_name: "",
    sponsor_logo_url: "",
    sponsor_website: ""
  });

  useEffect(() => {
    fetchPolls();
    fetchSponsors();
  }, []);

  const fetchPolls = async () => {
    try {
      const res = await fetch(`${API}/homepage-poll/admin/list`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPolls(data);
      }
    } catch (err) {
      console.error("Error fetching polls:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSponsors = async () => {
    try {
      const res = await fetch(`${API}/sponsors`);
      if (res.ok) {
        const data = await res.json();
        setSponsors(data || []);
      }
    } catch (err) {
      console.error("Error fetching sponsors:", err);
    }
  };

  const handleSponsorSelect = (sponsorId) => {
    if (!sponsorId) {
      setFormData({
        ...formData,
        sponsor_id: "",
        sponsor_name: "",
        sponsor_logo_url: "",
        sponsor_website: ""
      });
      return;
    }
    
    const sponsor = sponsors.find(s => s.id === sponsorId);
    if (sponsor) {
      setFormData({
        ...formData,
        sponsor_id: sponsor.id,
        sponsor_name: sponsor.name,
        sponsor_logo_url: sponsor.logo_url || "",
        sponsor_website: sponsor.website_url || ""
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty options
    const validOptions = formData.options.filter((opt) => opt.trim());
    if (validOptions.length < 2) {
      alert("Please provide at least 2 options");
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description || null,
      options: validOptions,
      expires_at: new Date(formData.expires_at).toISOString(),
      sponsor_id: formData.sponsor_id || null,
      sponsor_name: formData.sponsor_name || null,
      sponsor_logo_url: formData.sponsor_logo_url || null,
      sponsor_website: formData.sponsor_website || null
    };

    try {
      const url = editingPoll
        ? `${API}/homepage-poll/admin/${editingPoll.id}`
        : `${API}/homepage-poll/admin`;
      const method = editingPoll ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        fetchPolls();
        resetForm();
      } else {
        const err = await res.json();
        alert(err.detail || "Error saving poll");
      }
    } catch (err) {
      console.error("Error saving poll:", err);
    }
  };

  const handleDelete = async (pollId) => {
    if (!confirm("Are you sure you want to delete this poll?")) return;

    try {
      const res = await fetch(`${API}/homepage-poll/admin/${pollId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (res.ok) {
        fetchPolls();
      }
    } catch (err) {
      console.error("Error deleting poll:", err);
    }
  };

  const handleToggleActive = async (poll) => {
    try {
      const res = await fetch(`${API}/homepage-poll/admin/${poll.id}`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !poll.is_active }),
      });
      if (res.ok) {
        fetchPolls();
      }
    } catch (err) {
      console.error("Error toggling poll:", err);
    }
  };

  const handleResetVotes = async (pollId) => {
    if (!confirm("Are you sure you want to reset all votes on this poll?")) return;

    try {
      const res = await fetch(`${API}/homepage-poll/admin/${pollId}/reset`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (res.ok) {
        fetchPolls();
      }
    } catch (err) {
      console.error("Error resetting votes:", err);
    }
  };

  const handleEdit = (poll) => {
    setEditingPoll(poll);
    setFormData({
      title: poll.title,
      description: poll.description || "",
      options: poll.options.map((o) => o.text),
      expires_at: poll.expires_at ? poll.expires_at.slice(0, 16) : "",
      sponsor_id: poll.sponsor_id || "",
      sponsor_name: poll.sponsor_name || "",
      sponsor_logo_url: poll.sponsor_logo_url || "",
      sponsor_website: poll.sponsor_website || ""
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPoll(null);
    setFormData({
      title: "",
      description: "",
      options: ["", ""],
      expires_at: "",
      sponsor_id: "",
      sponsor_name: "",
      sponsor_logo_url: "",
      sponsor_website: ""
    });
  };

  const addOption = () => {
    if (formData.options.length < 5) {
      setFormData({ ...formData, options: [...formData.options, ""] });
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No expiration";
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (poll) => {
    if (poll.is_expired) {
      return (
        <span className="px-2 py-1 bg-zinc-700 text-zinc-300 text-xs font-bold uppercase rounded flex items-center gap-1">
          <Clock className="w-3 h-3" /> Expired
        </span>
      );
    }
    if (poll.is_active) {
      return (
        <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold uppercase rounded flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-zinc-600 text-zinc-300 text-xs font-bold uppercase rounded flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> Inactive
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-orange-500" />
          <h2 className="text-xl font-bold text-white">Homepage Poll</h2>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded transition-colors"
          data-testid="create-poll-btn"
        >
          <Plus className="w-4 h-4" />
          Create Poll
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-zinc-800 p-4 rounded-lg">
          <div className="text-2xl font-bold text-white">{polls.length}</div>
          <div className="text-zinc-400 text-sm">Total Polls</div>
        </div>
        <div className="bg-zinc-800 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-500">
            {polls.filter((p) => p.is_active && !p.is_expired).length}
          </div>
          <div className="text-zinc-400 text-sm">Active</div>
        </div>
        <div className="bg-zinc-800 p-4 rounded-lg">
          <div className="text-2xl font-bold text-orange-500">
            {polls.reduce((acc, p) => acc + p.total_votes, 0)}
          </div>
          <div className="text-zinc-400 text-sm">Total Votes</div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                {editingPoll ? "Edit Poll" : "Create New Poll"}
              </h3>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">
                  Poll Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500"
                  placeholder="Who will win tonight's game?"
                  required
                  maxLength={200}
                  data-testid="poll-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500 resize-none"
                  placeholder="Add more context to your poll..."
                  rows={2}
                  maxLength={500}
                  data-testid="poll-description-input"
                />
              </div>

              {/* Options */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Options (2-5) *
                </label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-zinc-500 text-sm w-6">{index + 1}.</span>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500"
                        placeholder={`Option ${index + 1}`}
                        maxLength={100}
                        data-testid={`poll-option-${index}`}
                      />
                      {formData.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="p-2 text-zinc-500 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {formData.options.length < 5 && (
                  <button
                    type="button"
                    onClick={addOption}
                    className="mt-2 text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add Option
                  </button>
                )}
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">
                  Expiration Date *
                </label>
                <input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500"
                  required
                  data-testid="poll-expires-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  After this date, only results will be shown
                </p>
              </div>

              {/* Sponsor Section */}
              <div className="pt-4 border-t border-zinc-700">
                <label className="flex items-center gap-2 text-sm font-medium text-zinc-300 mb-3">
                  <Award className="w-4 h-4 text-yellow-500" />
                  Poll Sponsor (Optional)
                </label>
                
                {/* Sponsor Selector */}
                <div className="mb-3">
                  <select
                    value={formData.sponsor_id}
                    onChange={(e) => handleSponsorSelect(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500"
                    data-testid="poll-sponsor-select"
                  >
                    <option value="">-- No Sponsor --</option>
                    {sponsors.map(sponsor => (
                      <option key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Sponsor Fields (if not selecting from list) */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Sponsor Name</label>
                    <input
                      type="text"
                      value={formData.sponsor_name}
                      onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                      placeholder="e.g., Acme Corp"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500 text-sm"
                      data-testid="poll-sponsor-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Logo URL</label>
                      <input
                        type="url"
                        value={formData.sponsor_logo_url}
                        onChange={(e) => setFormData({ ...formData, sponsor_logo_url: e.target.value })}
                        placeholder="https://..."
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500 text-sm"
                        data-testid="poll-sponsor-logo"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Website URL</label>
                      <input
                        type="url"
                        value={formData.sponsor_website}
                        onChange={(e) => setFormData({ ...formData, sponsor_website: e.target.value })}
                        placeholder="https://..."
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-orange-500 text-sm"
                        data-testid="poll-sponsor-website"
                      />
                    </div>
                  </div>
                  
                  {/* Sponsor Preview */}
                  {formData.sponsor_name && (
                    <div className="p-3 bg-zinc-900 rounded border border-zinc-700">
                      <div className="text-[10px] text-zinc-500 uppercase mb-2">Sponsor Preview</div>
                      <div className="flex items-center gap-3">
                        {formData.sponsor_logo_url && (
                          <img 
                            src={formData.sponsor_logo_url} 
                            alt="Sponsor logo" 
                            className="w-8 h-8 object-contain"
                          />
                        )}
                        <div className="flex-1">
                          <div className="text-sm text-white font-medium">{formData.sponsor_name}</div>
                          <div className="text-xs text-yellow-500">Presented by</div>
                        </div>
                        {formData.sponsor_website && (
                          <a 
                            href={formData.sponsor_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-zinc-400 hover:text-white"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white font-medium rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded transition-colors"
                  data-testid="poll-submit-btn"
                >
                  {editingPoll ? "Update Poll" : "Create Poll"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Polls List */}
      <div className="space-y-4">
        {polls.length === 0 ? (
          <div className="bg-zinc-800 rounded-lg p-8 text-center">
            <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No polls created yet</p>
            <p className="text-zinc-500 text-sm mt-1">
              Create a poll to engage with your audience
            </p>
          </div>
        ) : (
          polls.map((poll) => (
            <div
              key={poll.id}
              className={`bg-zinc-800 rounded-lg p-5 border-l-4 ${
                poll.is_active && !poll.is_expired
                  ? "border-green-500"
                  : poll.is_expired
                  ? "border-zinc-600"
                  : "border-zinc-700"
              }`}
              data-testid={`poll-card-${poll.id}`}
            >
              {/* Poll Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-white">{poll.title}</h3>
                    {getStatusBadge(poll)}
                  </div>
                  {poll.description && (
                    <p className="text-zinc-400 text-sm">{poll.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(poll)}
                    className={`p-2 rounded transition-colors ${
                      poll.is_active
                        ? "text-green-500 hover:bg-green-500/10"
                        : "text-zinc-500 hover:bg-zinc-700"
                    }`}
                    title={poll.is_active ? "Deactivate" : "Activate"}
                  >
                    {poll.is_active ? (
                      <ToggleRight className="w-5 h-5" />
                    ) : (
                      <ToggleLeft className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(poll)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleResetVotes(poll.id)}
                    className="p-2 text-zinc-400 hover:text-orange-500 hover:bg-zinc-700 rounded transition-colors"
                    title="Reset Votes"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(poll.id)}
                    className="p-2 text-zinc-400 hover:text-red-500 hover:bg-zinc-700 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Options with Results */}
              <div className="space-y-2 mb-4">
                {poll.options.map((option) => (
                  <div key={option.id} className="relative">
                    <div
                      className="absolute inset-0 bg-orange-500/20 rounded"
                      style={{ width: `${option.percentage || 0}%` }}
                    />
                    <div className="relative flex items-center justify-between px-3 py-2 bg-zinc-700/50 rounded">
                      <span className="text-white text-sm">{option.text}</span>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-zinc-400">{option.votes} votes</span>
                        <span className="text-orange-500 font-bold">
                          {option.percentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Poll Footer */}
              <div className="flex items-center justify-between text-sm text-zinc-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {poll.total_votes} total votes
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Expires: {formatDate(poll.expires_at)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HomepagePollTab;
